import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { type Asset, ShortUrlObjectType } from '@air/api/types';

import {
  type AssetShareLinkSelectionContext,
  type AssetShareLinkSlotId,
  getVersionClipIdForShareLinkSlot,
  resolveShareLinkObjectType,
} from '~/components/SharePrivateClipModal/buildAssetShareLinkSlots';
import { type PrivateAssetOpenLocation } from '~/hooks/usePrivateAssetOpenLocation';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { type ShortUrlDefaultSettingsResponse } from '~/swr-hooks/shortUrl/useGetDefaultSettings';
import { useGetShortIdForAsset } from '~/swr-hooks/shortUrl/useGetShortIdForAsset';
import { useGetShortIdForClip } from '~/swr-hooks/shortUrl/useGetShortIdForClip';
import {
  type AssetScopedShareLinkListItem,
  getAssetShareLinksListKey,
} from '~/swr-hooks/shortUrl/useListAssetShareLinks';
import { usePrivateVersionsList } from '~/swr-hooks/versions/usePrivateVersionsList';

type UseCreateShareLinkForSlotParams = {
  assetId: Asset['id'];
  assetShareLinksItems: AssetScopedShareLinkListItem[];
  analyticsLocation: PrivateAssetOpenLocation | null;
  canViewAssetVersions: boolean;
  defaultSettings: ShortUrlDefaultSettingsResponse | null | undefined;
  selectedSlotId: AssetShareLinkSlotId;
  selectionContext: AssetShareLinkSelectionContext;
};

export const useCreateShareLinkForSlot = ({
  assetId,
  assetShareLinksItems,
  analyticsLocation,
  canViewAssetVersions,
  defaultSettings,
  selectedSlotId,
  selectionContext,
}: UseCreateShareLinkForSlotParams) => {
  const { currentWorkspace } = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id;
  const queryClient = useQueryClient();
  const { mutateAsync: createShortIdForAsset, isPending: isCreatingShortIdForAsset } = useGetShortIdForAsset();
  const { mutateAsync: createShortIdForClip, isPending: isCreatingShortIdForClip } = useGetShortIdForClip();
  const needsVersionClipsList = useMemo(() => {
    if (selectedSlotId === 'current') {
      return false;
    }

    return getVersionClipIdForShareLinkSlot(selectedSlotId, selectionContext, assetShareLinksItems, []) === null;
  }, [assetShareLinksItems, selectedSlotId, selectionContext]);
  const { data: versionsList, isLoading: isLoadingVersionsList } = usePrivateVersionsList(
    { assetId, workspaceId },
    { enabled: needsVersionClipsList && canViewAssetVersions },
  );
  const versionClips = useMemo(() => versionsList?.data.clips ?? [], [versionsList?.data.clips]);
  const isLoadingVersionClips = needsVersionClipsList && isLoadingVersionsList;

  const createShareLinkForSlot = useCallback(async () => {
    if (!workspaceId) {
      throw new Error('No workspace id');
    }

    if (selectedSlotId === 'current') {
      return createShortIdForAsset({
        workspaceId,
        id: assetId,
        versionSlot: 'current',
        ...(analyticsLocation ?? {}),
      });
    }

    const versionClipId = getVersionClipIdForShareLinkSlot(
      selectedSlotId,
      selectionContext,
      assetShareLinksItems,
      versionClips,
    );

    if (!versionClipId) {
      throw new Error('No clip id for selected version slot');
    }

    if (resolveShareLinkObjectType({ slotId: selectedSlotId, defaultSettings }) === ShortUrlObjectType.asset) {
      return createShortIdForAsset({
        workspaceId,
        id: assetId,
        versionId: versionClipId,
        versionSlot: selectedSlotId,
        ...(analyticsLocation ?? {}),
        permissions: defaultSettings?.permissions,
      });
    }

    const response = await createShortIdForClip({
      workspaceId,
      id: versionClipId,
      versionSlot: selectedSlotId,
      ...(analyticsLocation ?? {}),
    });

    queryClient.invalidateQueries({
      queryKey: getAssetShareLinksListKey({ workspaceId, assetId }),
    });

    return response;
  }, [
    assetId,
    assetShareLinksItems,
    analyticsLocation,
    createShortIdForAsset,
    createShortIdForClip,
    defaultSettings,
    queryClient,
    selectedSlotId,
    selectionContext,
    versionClips,
    workspaceId,
  ]);

  return {
    createShareLinkForSlot,
    isCreatingShareLink: isCreatingShortIdForAsset || isCreatingShortIdForClip,
    isLoadingVersionClips,
  };
};
